import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  QrCode, 
  Download, 
  Copy, 
  Check, 
  Smartphone,
  Mail,
  MessageSquare,
  Users,
  Upload,
  Send,
  Sparkles,
  Link2,
  Share2,
  Instagram,
  Facebook,
  Twitter,
  Printer,
  CreditCard,
  Globe,
  Star,
  MapPin,
  Phone,
  Clock,
  Calendar,
  ExternalLink,
  UserPlus,
  FileText,
  Image as ImageIcon,
  Scissors
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

// QR Code Generator Component
export const QRCodeGenerator = ({ stylistId, stylistName, bookingUrl }) => {
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [qrStyle, setQrStyle] = useState("default"); // default, rounded, dots
  const [qrColor, setQrColor] = useState("#000000");
  const canvasRef = useRef(null);

  const baseUrl = window.location.origin;
  // Use guest booking URL - allows booking without account
  const fullBookingUrl = bookingUrl || `${baseUrl}/book-guest/${stylistId}`;

  const generateQR = async () => {
    setLoading(true);
    try {
      // Use a QR code API or generate client-side
      const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(fullBookingUrl)}&color=${qrColor.replace('#', '')}&bgcolor=FFFFFF&margin=10`;
      setQrDataUrl(qrApiUrl);
    } catch (error) {
      toast.error("Failed to generate QR code");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    generateQR();
  }, [fullBookingUrl, qrColor]);

  const downloadQR = () => {
    if (!qrDataUrl) return;
    
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `${stylistName || 'booking'}-qr-code.png`;
    link.click();
    toast.success("QR Code downloaded!");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullBookingUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const QR_COLORS = [
    { name: "Black", value: "#000000" },
    { name: "Amber", value: "#f59e0b" },
    { name: "Purple", value: "#7c3aed" },
    { name: "Emerald", value: "#10b981" },
    { name: "Red", value: "#ef4444" },
    { name: "Blue", value: "#3b82f6" }
  ];

  return (
    <Card className="bg-stone-800/50 border-stone-700">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <QrCode className="w-5 h-5 text-amber-400" />
          QR Code Generator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* QR Code Display */}
        <div className="flex flex-col items-center">
          <div className="bg-white p-4 rounded-2xl shadow-lg">
            {loading ? (
              <div className="w-[200px] h-[200px] flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : qrDataUrl ? (
              <img 
                src={qrDataUrl} 
                alt="Booking QR Code" 
                className="w-[200px] h-[200px]"
              />
            ) : (
              <div className="w-[200px] h-[200px] bg-stone-200 flex items-center justify-center">
                <QrCode className="w-16 h-16 text-stone-400" />
              </div>
            )}
          </div>
          <p className="text-sm text-gray-400 mt-3 text-center">
            Clients scan this to book instantly
          </p>
        </div>

        {/* Color Selection */}
        <div>
          <Label className="text-gray-300 mb-2 block">QR Code Color</Label>
          <div className="flex gap-2 flex-wrap">
            {QR_COLORS.map(color => (
              <button
                key={color.value}
                onClick={() => setQrColor(color.value)}
                className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                  qrColor === color.value ? 'border-white scale-110' : 'border-transparent'
                }`}
                style={{ backgroundColor: color.value }}
                title={color.name}
              />
            ))}
          </div>
        </div>

        {/* Booking Link */}
        <div>
          <Label className="text-gray-300 mb-2 block">Your Booking Link</Label>
          <div className="flex gap-2">
            <Input 
              value={fullBookingUrl}
              readOnly
              className="bg-stone-900 border-stone-600 text-white text-sm"
            />
            <Button 
              variant="outline" 
              size="icon"
              onClick={copyLink}
              className="border-stone-600 hover:bg-stone-700"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
            </Button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <Button 
            onClick={downloadQR}
            className="bg-amber-500 hover:bg-amber-600 text-black"
          >
            <Download className="w-4 h-4 mr-2" />
            Download QR
          </Button>
          <Button 
            variant="outline"
            onClick={() => window.print()}
            className="border-stone-600 text-white hover:bg-stone-700"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>

        {/* Usage Tips */}
        <div className="bg-stone-900 rounded-xl p-4">
          <h4 className="text-white font-medium mb-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400" />
            Where to Use Your QR Code
          </h4>
          <ul className="space-y-1 text-sm text-gray-400">
            <li>• Display at your station/mirror</li>
            <li>• Print on business cards</li>
            <li>• Add to flyers & posters</li>
            <li>• Include in thank-you cards</li>
            <li>• Post on social media</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

// SMS/Email Blast Component
export const ClientInviteBlast = ({ token, stylistName }) => {
  const [activeTab, setActiveTab] = useState("manual");
  const [contacts, setContacts] = useState([]);
  const [manualInput, setManualInput] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(0);
  const [showPreview, setShowPreview] = useState(false);

  const baseUrl = window.location.origin;
  // Use guest booking URL - allows booking without account
  const bookingLink = `${baseUrl}/book-guest/${stylistId}`;

  const DEFAULT_MESSAGES = {
    sms: `Hey! I'm now on MyHairChair - book your next appointment online anytime! 💈\n\nNo account needed - just pick a time:\n${bookingLink}\n\n- ${stylistName || 'Your Stylist'}`,
    email: `Hi!\n\nGreat news - you can now book appointments with me online anytime, 24/7!\n\nNo account needed! Just click the link below to see my availability and book instantly:\n\n${bookingLink}\n\nI look forward to seeing you soon!\n\n${stylistName || 'Your Stylist'}`
  };

  useEffect(() => {
    setMessage(DEFAULT_MESSAGES.sms);
  }, [stylistName]);

  const parseContacts = (input) => {
    // Parse phone numbers and emails from input
    const lines = input.split(/[\n,;]+/);
    const parsed = [];
    
    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed) return;
      
      // Check if email
      if (trimmed.includes('@')) {
        parsed.push({ type: 'email', value: trimmed, name: '' });
      } 
      // Check if phone (10+ digits)
      else {
        const digits = trimmed.replace(/\D/g, '');
        if (digits.length >= 10) {
          parsed.push({ type: 'phone', value: digits, name: '' });
        }
      }
    });
    
    return parsed;
  };

  const handleInputChange = (e) => {
    setManualInput(e.target.value);
    setContacts(parseContacts(e.target.value));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      setManualInput(text);
      setContacts(parseContacts(text));
      toast.success(`Loaded ${parseContacts(text).length} contacts`);
    } catch {
      toast.error("Failed to read file");
    }
  };

  const sendInvites = async () => {
    if (contacts.length === 0) {
      toast.error("No valid contacts to send to");
      return;
    }

    setSending(true);
    setSent(0);

    try {
      const phones = contacts.filter(c => c.type === 'phone').map(c => c.value);
      const emails = contacts.filter(c => c.type === 'email').map(c => c.value);

      // Send SMS invites
      if (phones.length > 0) {
        await axios.post(`${API}/invites/sms-blast`, {
          phone_numbers: phones,
          message: message
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSent(prev => prev + phones.length);
      }

      // Send Email invites
      if (emails.length > 0) {
        await axios.post(`${API}/invites/email-blast`, {
          emails: emails,
          subject: `Book with ${stylistName || 'me'} online!`,
          message: message
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSent(prev => prev + emails.length);
      }

      toast.success(`Sent ${contacts.length} invites!`);
      setManualInput("");
      setContacts([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invites");
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="bg-stone-800/50 border-stone-700">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Send className="w-5 h-5 text-amber-400" />
          Invite Your Clients
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-stone-900 border-stone-700">
            <TabsTrigger value="manual" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              <Users className="w-4 h-4 mr-2" />
              Enter Contacts
            </TabsTrigger>
            <TabsTrigger value="upload" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              <Upload className="w-4 h-4 mr-2" />
              Upload List
            </TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="mt-4">
            <div>
              <Label className="text-gray-300 mb-2 block">
                Phone Numbers & Emails
                <span className="text-gray-500 text-xs ml-2">(one per line or comma-separated)</span>
              </Label>
              <Textarea 
                value={manualInput}
                onChange={handleInputChange}
                placeholder="(555) 123-4567&#10;client@email.com&#10;555-987-6543&#10;another@email.com"
                className="bg-stone-900 border-stone-600 text-white h-32"
              />
            </div>
          </TabsContent>

          <TabsContent value="upload" className="mt-4">
            <div className="border-2 border-dashed border-stone-600 rounded-xl p-8 text-center">
              <Upload className="w-12 h-12 text-stone-500 mx-auto mb-3" />
              <p className="text-gray-400 mb-3">Upload a CSV or TXT file with contacts</p>
              <input 
                type="file" 
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="hidden"
                id="contact-upload"
              />
              <label htmlFor="contact-upload">
                <Button variant="outline" className="border-stone-600 text-white cursor-pointer" asChild>
                  <span>Choose File</span>
                </Button>
              </label>
            </div>
          </TabsContent>
        </Tabs>

        {/* Contact Count */}
        {contacts.length > 0 && (
          <div className="flex items-center gap-4 p-3 bg-stone-900 rounded-lg">
            <Badge className="bg-amber-500/20 text-amber-400">
              {contacts.filter(c => c.type === 'phone').length} phones
            </Badge>
            <Badge className="bg-blue-500/20 text-blue-400">
              {contacts.filter(c => c.type === 'email').length} emails
            </Badge>
            <span className="text-gray-400 text-sm ml-auto">
              {contacts.length} total contacts
            </span>
          </div>
        )}

        {/* Message */}
        <div>
          <Label className="text-gray-300 mb-2 block">Invitation Message</Label>
          <Textarea 
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="bg-stone-900 border-stone-600 text-white h-32"
            placeholder="Your invitation message..."
          />
          <p className="text-xs text-gray-500 mt-1">
            Your booking link will be automatically included
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button 
            onClick={() => setShowPreview(true)}
            variant="outline"
            className="flex-1 border-stone-600 text-white hover:bg-stone-700"
          >
            Preview
          </Button>
          <Button 
            onClick={sendInvites}
            disabled={contacts.length === 0 || sending}
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-black"
          >
            {sending ? (
              <>
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send {contacts.length} Invites
              </>
            )}
          </Button>
        </div>

        {/* Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="bg-stone-900 border-stone-700 text-white">
            <DialogHeader>
              <DialogTitle>Message Preview</DialogTitle>
            </DialogHeader>
            <div className="bg-stone-800 rounded-lg p-4">
              <p className="whitespace-pre-wrap text-gray-300">{message}</p>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

// Digital Business Card Component
export const DigitalBusinessCard = ({ profile, token }) => {
  const [copied, setCopied] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  
  const baseUrl = window.location.origin;
  const cardUrl = `${baseUrl}/book?stylist=${profile?.user_id || 'me'}`;
  
  // QR Code URL
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(cardUrl)}&bgcolor=1c1917&color=f59e0b&margin=5`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(cardUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const shareCard = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile?.business_name || profile?.user?.name || 'Stylist'} - Book Now`,
          text: `Check out my profile and book an appointment!`,
          url: cardUrl
        });
      } catch {
        // User cancelled
      }
    } else {
      setShowShareOptions(true);
    }
  };
  
  const downloadCard = () => {
    // Create a canvas to combine card elements for download
    toast.success("Right-click the card to save as image, or use screenshot!");
  };

  const socialLinks = [
    { name: 'Instagram', icon: Instagram, url: `https://www.instagram.com/?url=${encodeURIComponent(cardUrl)}`, color: 'bg-pink-500' },
    { name: 'Facebook', icon: Facebook, url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(cardUrl)}`, color: 'bg-blue-600' },
    { name: 'Twitter', icon: Twitter, url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(cardUrl)}&text=${encodeURIComponent(`Book with me on MyHairChair!`)}`, color: 'bg-sky-500' },
    { name: 'WhatsApp', icon: MessageSquare, url: `https://wa.me/?text=${encodeURIComponent(`Book with me: ${cardUrl}`)}`, color: 'bg-green-500' },
    { name: 'Email', icon: Mail, url: `mailto:?subject=${encodeURIComponent(`Book with ${profile?.user?.name || 'me'}`)}&body=${encodeURIComponent(`Check out my profile and book an appointment: ${cardUrl}`)}`, color: 'bg-gray-600' }
  ];

  return (
    <Card className="bg-stone-800/50 border-stone-700">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-amber-400" />
          Digital Business Card
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Card Preview */}
        <div className="bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 rounded-2xl p-6 border border-stone-700 shadow-xl">
          {/* Header with Profile and QR Code */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                {profile?.portfolio_images?.[0] ? (
                  <img 
                    src={profile.portfolio_images[0]} 
                    alt={profile?.user?.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <Scissors className="w-8 h-8 text-white" />
                )}
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">
                  {profile?.business_name || profile?.user?.name || 'Your Name'}
                </h3>
                <p className="text-amber-400 text-sm">
                  {profile?.professional_type || 'Barber/Stylist'}
                </p>
              </div>
            </div>
            
            {/* QR Code on Card */}
            <div className="bg-stone-800 p-2 rounded-xl">
              <img 
                src={qrCodeUrl}
                alt="Scan to Book"
                className="w-20 h-20 rounded"
              />
              <p className="text-[10px] text-center text-gray-500 mt-1">Scan to Book</p>
            </div>
          </div>

          {/* Info */}
          <div className="space-y-2 mb-4">
            {profile?.address && (
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <MapPin className="w-4 h-4" />
                <span>{profile.address}, {profile.city}</span>
              </div>
            )}
            {profile?.user?.phone && (
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Phone className="w-4 h-4" />
                <span>{profile.user.phone}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <Globe className="w-4 h-4" />
              <span className="text-amber-400 text-xs">{cardUrl.replace('https://', '')}</span>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="bg-stone-900/50 rounded-lg p-2 text-center">
              <p className="text-xl font-bold text-white">{profile?.rating?.toFixed(1) || '5.0'}</p>
              <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                <Star className="w-3 h-3 text-amber-400" /> Rating
              </p>
            </div>
            <div className="bg-stone-900/50 rounded-lg p-2 text-center">
              <p className="text-xl font-bold text-white">{profile?.total_reviews || 0}</p>
              <p className="text-xs text-gray-500">Reviews</p>
            </div>
            <div className="bg-stone-900/50 rounded-lg p-2 text-center">
              <p className="text-xl font-bold text-white">{profile?.experience_years || 0}+</p>
              <p className="text-xs text-gray-500">Years</p>
            </div>
          </div>

          {/* CTA */}
          <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold">
            Book Appointment
          </Button>
        </div>
        
        {/* Larger QR Code for Printing */}
        <div className="bg-stone-900 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-white font-medium flex items-center gap-2">
              <QrCode className="w-4 h-4 text-amber-400" />
              Printable QR Code
            </h4>
            <a 
              href={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(cardUrl)}&bgcolor=FFFFFF&color=000000&margin=10`}
              download={`${profile?.user?.name || 'booking'}-qr.png`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="sm" variant="outline" className="border-stone-600 text-gray-300 hover:bg-stone-700">
                <Download className="w-4 h-4 mr-1" />
                Download
              </Button>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <div className="bg-white p-3 rounded-xl">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(cardUrl)}&bgcolor=FFFFFF&color=000000&margin=5`}
                alt="QR Code"
                className="w-24 h-24"
              />
            </div>
            <div className="flex-1 text-sm text-gray-400">
              <p className="mb-2">Print this QR code and display it:</p>
              <ul className="space-y-1 text-xs">
                <li>• At your station/mirror</li>
                <li>• On business cards</li>
                <li>• In your shop window</li>
                <li>• On receipts & flyers</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Card URL */}
        <div>
          <Label className="text-gray-300 mb-2 block">Your Booking Link</Label>
          <div className="flex gap-2">
            <Input 
              value={cardUrl}
              readOnly
              className="bg-stone-900 border-stone-600 text-white text-sm"
            />
            <Button 
              variant="outline" 
              size="icon"
              onClick={copyLink}
              className="border-stone-600 hover:bg-stone-700"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
            </Button>
          </div>
        </div>

        {/* Share Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <Button 
            onClick={shareCard}
            className="bg-amber-500 hover:bg-amber-600 text-black"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share Card
          </Button>
          <Button 
            variant="outline"
            onClick={() => setShowShareOptions(true)}
            className="border-stone-600 text-white hover:bg-stone-700"
          >
            <Users className="w-4 h-4 mr-2" />
            Social Links
          </Button>
        </div>

        {/* Social Share Dialog */}
        <Dialog open={showShareOptions} onOpenChange={setShowShareOptions}>
          <DialogContent className="bg-stone-900 border-stone-700 text-white">
            <DialogHeader>
              <DialogTitle>Share Your Card</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {socialLinks.map(social => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-3 p-3 rounded-lg ${social.color} text-white hover:opacity-90 transition-opacity`}
                >
                  <social.icon className="w-5 h-5" />
                  <span>{social.name}</span>
                </a>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Tips */}
        <div className="bg-stone-900 rounded-xl p-4">
          <h4 className="text-white font-medium mb-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400" />
            Pro Tips
          </h4>
          <ul className="space-y-1 text-sm text-gray-400">
            <li>• Add this link to your Instagram bio</li>
            <li>• Share in your Stories with a &quot;Book Now&quot; sticker</li>
            <li>• Text it to clients after appointments</li>
            <li>• Include in email signatures</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

// Combined Client Tools Component
const ClientInviteTools = ({ token, profile, stylistId, stylistName }) => {
  const [activeTab, setActiveTab] = useState("qr");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <UserPlus className="w-6 h-6 text-amber-400" />
          Client Invitation Tools
        </h2>
        <Badge className="bg-amber-500/20 text-amber-400">
          <Sparkles className="w-3 h-3 mr-1" />
          Grow Your Clientele
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-stone-800 border-stone-700 w-full grid grid-cols-3">
          <TabsTrigger value="qr" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <QrCode className="w-4 h-4 mr-2" />
            QR Code
          </TabsTrigger>
          <TabsTrigger value="invite" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <Send className="w-4 h-4 mr-2" />
            Invite Blast
          </TabsTrigger>
          <TabsTrigger value="card" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <CreditCard className="w-4 h-4 mr-2" />
            Business Card
          </TabsTrigger>
        </TabsList>

        <TabsContent value="qr" className="mt-6">
          <QRCodeGenerator 
            stylistId={stylistId}
            stylistName={stylistName}
          />
        </TabsContent>

        <TabsContent value="invite" className="mt-6">
          <ClientInviteBlast 
            token={token}
            stylistName={stylistName}
          />
        </TabsContent>

        <TabsContent value="card" className="mt-6">
          <DigitalBusinessCard 
            profile={profile}
            token={token}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClientInviteTools;
